import { Injectable } from '@angular/core';
import {
    addDoc,
    collection,
    collectionData,
    CollectionReference,
    deleteDoc,
    doc,
    docData,
    DocumentData,
    DocumentReference,
    Firestore,
    query,
    updateDoc,
    where,
} from '@angular/fire/firestore';
import { from, Observable, switchMap } from 'rxjs';
import { OrganizationData } from '../shared/models/organization-data.model';
import { UserRole } from '../shared/models/user.model';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root',
})
export class OrganizationService {
    private collectionPath: string = 'organizations';
    private systemsCollection: CollectionReference;

    constructor(
        private readonly firestore: Firestore,
        private authService: AuthService
    ) {
        this.systemsCollection = collection(this.firestore, 'organizations');
    }

    getOrganizations(): Observable<OrganizationData[]> {
        return this.authService.getAuthenticatedUserRole().pipe(
            switchMap((userRole) => {
                if (userRole === UserRole.SUPER_ADMIN) {
                    const colRef = collection(this.firestore, 'organizations');
                    return collectionData(colRef, { idField: 'id' }) as Observable<OrganizationData[]>;
                }

                return this.authService
                    .getAuthenticatedUserUid()
                    .pipe(
                        switchMap((userId) => {
                            const colRef = collection(this.firestore, 'organizations');
                            const q = query(colRef, where('userId', '==', userId));
                            return collectionData(q, { idField: 'id' }) as Observable<OrganizationData[]>;
                        })
                    );
            })
        );
    }

    createOrganization(systemData: OrganizationData): Observable<DocumentReference<DocumentData, DocumentData>> {
        return from(
            addDoc(this.systemsCollection, <OrganizationData>{
                ...systemData,
                status: 'active',
                verificationStage: 'pending',
                createdAt: new Date(),
            })
        );
    }

    deleteOrganization(id: string): Promise<void> {
        const orgDocRef = doc(this.firestore, `organizations/${id}`);
        return deleteDoc(orgDocRef);
    }

    findOrganizationByKey(organizationKey: string): Observable<OrganizationData | undefined> {
        const docRef = doc(this.firestore, `${this.collectionPath}/${organizationKey}`);
        return docData(docRef) as Observable<OrganizationData | undefined>;
    }

    updateOrganization(organizationKey: string, organizationData: Partial<OrganizationData>): Observable<void> {
        if (organizationData?.organizationName) {
            delete organizationData.organizationName;
        }

        const docRef = doc(this.firestore, `${this.collectionPath}/${organizationKey}`);
        return from(updateDoc(docRef, organizationData));
    }
}
